import UserIconSVG from "images/Icons/UserL.inline.svg"
import UsersIconSVG from "images/Icons/UsersL.inline.svg"

import PinSVG from "images/Icons/Pin.inline.svg"
import BlipCirclesSVG from "images/home/features/blipCircles.inline.svg"
import TagBlipSVG from "images/home/features/tagBlip.inline.svg"
import { css, fmobile, fresponsive, styled } from "library/styled"
import type { ReactNode } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

export default function ExtendedTag({
	children,
	info,
	className,
}: {
	children: ReactNode
	info?: { businessIcon: ReactNode; businessType: string; location: string }
	className?: string
}) {
	return (
		<Wrapper className={className}>
			{info && (
				<BlipCircles className="blip-circles">
					<BlipCirclesSVG />
				</BlipCircles>
			)}
			{info && (
				<TagBlip className="tag-blip">
					<TagBlipSVG />
				</TagBlip>
			)}
			<InnerWrapper className="tag-border">
				<Square className="extended-tag-square">
					<Inner>
						<Row>
							<IconRow>
								<IconWrapper className="extended-tag-icon-wrapper">
									{info ? <UserIcon className="user-icon-l" /> : <UsersIcon />}
								</IconWrapper>

								<Text>{children}</Text>
							</IconRow>
							{info && (
								<Info>
									<SmallRow>
										<SmallIcon>{info?.businessIcon} </SmallIcon>
										<BusinessText>{info?.businessType}</BusinessText>
									</SmallRow>
									<SmallRow>
										<SmallIcon>
											<Pin />
										</SmallIcon>
										<BusinessText>{info?.location}</BusinessText>
									</SmallRow>
								</Info>
							)}
						</Row>
					</Inner>
				</Square>
			</InnerWrapper>
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...fresponsive(css`
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		height: 60px;
		width: 60px;
	`),
})

const Square = styled("div", {
	...fresponsive(css`
		position: relative;
		border-radius: 4px;
		width: 100%;
		height: 100%;
		z-index: 1;
		overflow: clip;
		background: linear-gradient(
			180deg,
			#fbfaf6 -48.96%,
			var(--gradient-end, #cdcad6)
		);
		box-shadow: 0 4px 4px 0 rgba(255 255 255 / 25%) inset;
	`),
})

const Inner = styled("div", {
	...fresponsive(css`
		position: absolute;
		border-radius: 4px;
		z-index: 1;
		display: flex;
		align-items: center;
		left: 0;
		top: 0;
		gap: 2px;
		padding: 12px 12px 12px 0;
		height: 48px;
		width: 322px;
	`),
})

const InnerWrapper = styled("div", {
	...fresponsive(css`
		position: relative;
		z-index: 2;
		overflow: clip;
		height: 100%;
		width: 100%;
		border-radius: 8px;
		border: 1px dashed ${colors.purple200};
		background: ${colors.offWhite};
		padding: 6px;
	`),
})

const Text = styled("span", {
	...fresponsive(css`
		position: relative;
		${textStyles.custom.bodyXXXS};
		color: ${colors.green400};
		padding: 4px 0;
		margin: -4px 0;
		z-index: 2;
	`),
	...fmobile(css`
		color: ${colors.orange};
	`),
})

const UserIcon = styled(UserIconSVG, {
	...fresponsive(css`
		width: 24px;
		height: 24px;
	`),
	...fmobile(css`
		width: 16px;
		height: 16px;
		color: ${colors.orange};
	`),
})

const UsersIcon = styled(UsersIconSVG, {
	...fresponsive(css`
		width: 24px;
		height: 24px;
		color: ${colors.purple300};
	`),
	...fmobile(css`
		width: 16px;
		height: 16px;
		color: ${colors.orange};
	`),
})

const Info = styled("div", {
	...fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-left: 8px;
		flex-shrink: 0;
	`),
})

const Row = styled("div", {
	...fresponsive(css`
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
	`),
})

const IconRow = styled("div", {
	...fresponsive(css`
		display: flex;
		align-items: center;
	`),
})

const SmallRow = styled("div", {
	...fresponsive(css`
		display: flex;
		align-items: center;
		gap: 4px;
		width: 100%;
		height: 8px;
	`),
})

const Pin = styled(PinSVG, {
	...fresponsive(css`
		width: 7px;
	`),
	...fmobile(css`
		width: 16px;
		height: 16px;
	`),
})

const SmallIcon = styled("div", {
	...fresponsive(css`
		width: 8px;
		height: 8px;
		position: relative;
		color: ${colors.green400};
		display: grid;
		place-items: center;

		svg {
			position: absolute;
			top: 0;
			left: 0;
			width: 7px;
			height: 7px;
		}
	`),
	...fmobile(css`
		width: 16px;
		height: 16px;
	`),
})

const BusinessText = styled("span", {
	...fresponsive(css`
		position: relative;
		${textStyles.custom.bodyXXXXS};
		color: ${colors.green400};
	`),
	...fmobile(css`
		color: ${colors.orange};
	`),
})

const TagBlip = styled("div", {
	...fresponsive(css`
		position: absolute;
		bottom: -5.5px;
		left: 24px;
		width: 12px;
		height: 12px;
		z-index: 10;
		transform: scale(0);
		transform-origin: center;

		svg {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			will-change: filter;
		}
	`),
})

const BlipCircles = styled("div", {
	...fresponsive(css`
		position: absolute;
		bottom: -24px;
		left: 10px;
		width: 40px;
		height: 40px;
		z-index: 0;
		transform: scale(0);
		background: #0080ab20;
		border-radius: 50%;
		backdrop-filter: blur(10px);

		svg {
			width: 100%;
			height: 100%;
		}
	`),
})

const IconWrapper = styled("div", {
	...fresponsive(css`
		width: 48px;
		height: 48px;
		display: grid;
		place-items: center;
	`),
})
